import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';

import { AppTopBar } from '@/app/layout/AppTopBar';
import { WORKSPACE_PAGE_GRADIENT } from '@/app/layout/constants';
import { DashboardSidebar } from '@/app/layout/DashboardSidebar';
import { paths } from '@/app/routes/paths';
import {
  currentOnboardingStep,
  isGuardianPortalOnly,
  isOnboardingComplete,
  useAuthStore,
} from '@/store';

import { onboardingPathForStep } from '../lib/onboarding-routing';

/**
 * Layout for the organisation onboarding flow. Mirrors the dashboard chrome
 * (top bar + hub sidebar) but keeps the sidebar visible regardless of the
 * organisation's email-verification state so the user can track progress
 * while they complete setup.
 *
 * On initial mount (fresh page load / hard refresh) the layout redirects to
 * the step the backend currently treats as "current" (the resume pointer)
 * so reloading on a completed step bounces the user forward to where they
 * actually need to be. Subsequent client-side navigations — e.g. clicking a
 * completed row in the Progress Status card to revisit a prior step — are
 * left untouched.
 */
export function OnboardingLayout() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();
  const didInitialRedirect = useRef(false);

  useEffect(() => {
    if (didInitialRedirect.current) return;
    if (!user) return;

    didInitialRedirect.current = true;
    const resumeStep = currentOnboardingStep(user);
    // Onboarding already finished — leave the user on whatever onboarding
    // sub-route they hit (e.g. /onboarding/complete). The dashboard gate
    // handles pushing finished users away from /onboarding root.
    if (resumeStep === null) return;

    const target = onboardingPathForStep(resumeStep);
    if (target && target !== location.pathname) {
      void navigate(target, { replace: true });
    }
  }, [user, location.pathname, navigate]);

  if (user && isGuardianPortalOnly(user)) {
    return <Navigate to={paths.guardianDashboard} replace />;
  }

  // Hard lock: once onboarding is complete, step pages (identity/service
  // sites/your team/first intake) must not be editable anymore. Any direct
  // navigation to them is redirected to the dashboard. The `/onboarding/
  // complete` success page is still reachable so users coming from Finish
  // Onboarding can see it (and deep-links to it stay harmless).
  if (user && isOnboardingComplete(user) && location.pathname !== paths.onboardingComplete) {
    return <Navigate to={paths.dashboard} replace />;
  }

  return (
    <div
      className="flex h-dvh w-full flex-col overflow-hidden"
      style={{ backgroundImage: WORKSPACE_PAGE_GRADIENT }}
    >
      <AppTopBar userName={user?.fullName ?? ''} logoTo={paths.onboardingIdentity} />
      <main className="flex min-h-0 flex-1">
        <DashboardSidebar />
        <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
          <Outlet />
        </section>
      </main>
    </div>
  );
}
